import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Instagram } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { API_ENDPOINTS } from "../lib/api";

/**
 * Instagram OAuth callback. Instagram redirects here (IG_REDIRECT_URI) with a
 * code; we exchange it via /instagram/oauth/exchange, then return to the unified
 * pages list. Mirrors FacebookCallbackPage but Instagram-native.
 */
const InstagramCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const [status, setStatus] = useState<"loading" | "success" | "error" | "denied">("loading");
  const [message, setMessage] = useState("Connecting your Instagram account...");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (isLoading || hasProcessed.current) return;

    const run = async () => {
      try {
        if (!isAuthenticated) {
          setStatus("error");
          setMessage("You must be logged in to connect Instagram. Please log in first.");
          return;
        }
        hasProcessed.current = true;

        const error = searchParams.get("error");
        const code = searchParams.get("code");
        const errorReason = searchParams.get("error_reason");
        const errorDescription = searchParams.get("error_description");

        if (error === "access_denied" || errorReason === "user_denied") {
          setStatus("denied");
          setMessage("You cancelled the Instagram authorization.");
          return;
        }
        if (error) {
          setStatus("error");
          setMessage(`Instagram authorization failed: ${errorDescription || error}`);
          return;
        }
        if (!code) {
          setStatus("error");
          setMessage("No authorization code received from Instagram.");
          return;
        }

        setMessage("Exchanging authorization code...");
        const token = await getAccessTokenSilently();
        const response = await axios.post(
          API_ENDPOINTS.INSTAGRAM_OAUTH_EXCHANGE,
          { code, state: searchParams.get("state") },
          {
            baseURL: process.env.REACT_APP_API_URL || "",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );

        if (response.data.success) {
          if (response.data.requiresSignup) {
            setStatus("success");
            setMessage("Instagram connected! Please complete your profile to continue.");
            setTimeout(() => navigate("/signup?redirect=/accounts"), 2000);
            return;
          }
          setStatus("success");
          setMessage("Instagram account connected successfully!");
          setTimeout(() => navigate("/accounts"), 1500);
        } else {
          setStatus("error");
          setMessage(response.data.error || "Failed to connect Instagram. Please try again.");
        }
      } catch (err: any) {
        console.error("❌ [IG_CALLBACK] Exchange error:", err);
        setStatus("error");
        setMessage(err.response?.data?.error || "Failed to connect Instagram. Please try again.");
      }
    };

    run();
  }, [isLoading, isAuthenticated, searchParams, getAccessTokenSilently, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
              <Instagram className="h-6 w-6 text-white" />
            </div>
            <CardTitle>Instagram</CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {status === "loading" && <Loader2 className="h-8 w-8 animate-spin text-pink-500" />}
            {status === "success" && <CheckCircle className="h-8 w-8 text-green-500" />}
            {(status === "error" || status === "denied") && (
              <>
                <XCircle className="h-8 w-8 text-red-500" />
                <Button onClick={() => navigate("/accounts")} variant="outline" className="w-full">
                  Back to pages
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default InstagramCallbackPage;
